// @ts-strict-ignore
import { PrivilegesModal } from './Modal'
import { useNotifications } from '@components/NotificationsProvider'
import RichContentField from '@components/_content-page/RichContentField'
import { Button, Icon, Image, SpecialSpan, VideoPlayer } from '@components/gassan-ui'
import { Heading } from '@components/gassan-ui/Typography/Heading'
import { PrivilegesQuery } from '@generated'
import { cn } from '@lib/cn'
import { useAddPrivilegeItemToCart } from '@lib/hooks/use-add-privilege-item-to-cart'
import { useCart } from '@lib/hooks/useCart'
import { getKontentImage } from '@lib/kontent'
import { PrivilegeItem as PrivilegeItemModel } from '@lib/kontent/models/PrivilegeItem'
import { useTranslation } from 'next-i18next'
import { FC, useEffect, useState } from 'react'
import { linariaTheme } from '@config/theme'

type PrivilegeItemProps = {
  modularContent: {
    [key: string]: any
  }
  elements: PrivilegeItemModel
  isAvailable: boolean
  dafItem: PrivilegesQuery['privileges'][0]
}

export const PrivilegeItem: FC<PrivilegeItemProps> = ({
  elements,
  modularContent,
  isAvailable,
  dafItem,
}) => {
  const [modalDescription, setModalDescription] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'redeemed' | 'in-cart' | 'loading'>('idle')
  const { t } = useTranslation(['user-errors', 'other', 'navigation', 'account'])
  const { addNotification } = useNotifications()
  const { addPrivilegeItemToCart } = useAddPrivilegeItemToCart()
  const cart = useCart()

  const richContent = modularContent[elements.rich_content.value[0]]
  const hasVideo = !!elements.video.value[0]

  useEffect(() => {
    if (dafItem.privilege_action === 'SALESORDER') {
      // If the item is a sales order, we need to check if the cart already contains the item
      // and, if so, check if the quantity in the cart does not exceed the maximum allowed quantity
      if (cart && cart.privilegeItems) {
        const currentItemsAlreadyInCart = cart.privilegeItems.filter(
          (item) => item.privilegeId === dafItem.privilege_nr.toString(),
        )
        if (currentItemsAlreadyInCart.length < dafItem.beschikbaar) {
          setStatus('idle')
        } else {
          setStatus('in-cart')
        }
      }
    } else {
      setStatus(isAvailable ? 'idle' : 'redeemed')
    }
  }, [cart]) // eslint-disable-line

  if (richContent.elements.content.type !== 'rich_text') return null

  return (
    <>
      <div className="flex flex-col border border-shade-300">
        <div className="w-full px-4 py-5 md:px-6 md:py-8 lg:px-10 lg:py-10 [&>#video-player]:lg:h-full">
          {hasVideo ? (
            <VideoPlayer
              placeholderSrc={getKontentImage(elements.image___placeholder_image.value[0].url, {
                width: 30,
              })}
              images={[
                getKontentImage(elements.image___placeholder_image.value[0].url, { width: 768 }),
                getKontentImage(elements.image___placeholder_image.value[0].url, { width: 1024 }),
              ]}
              videoSrc={elements.video.value[0].url}
              height="100%"
              width="100%"
            />
          ) : (
            <Image
              alt={elements.image___placeholder_image.value[0].description}
              sources={[
                getKontentImage(elements.image___placeholder_image.value[0].url, { width: 768 }),
                getKontentImage(elements.image___placeholder_image.value[0].url, { width: 1024 }),
              ]}
              placeholder={getKontentImage(elements.image___placeholder_image.value[0].url, {
                width: 30,
              })}
              className={cn('aspect-video h-full w-full object-cover')}
            />
          )}
        </div>
        <div className="flex flex-1 flex-col items-start px-4 pb-5 md:px-6 md:pb-8 lg:px-10 lg:pb-10 [&>[data-bottom-element]]:mt-auto [&>[data-bottom-element]]:w-full md:[&>[data-bottom-element]]:w-auto">
          <Heading variant="h3" as="h3" className="mb-4 md:mb-6">
            {elements.title.value}
          </Heading>
          <div className="mb-6">
            <RichContentField {...richContent.elements.content} />
          </div>
          {['redeemed', 'in-cart'].includes(status) ? (
            <div
              className="flex items-center text-bigStone [&>span]:!text-[11px]"
              data-bottom-element
            >
              <Icon icon="account-check" color={linariaTheme.colors.success} />
              <SpecialSpan variant="two">
                {t(`privileges.${status}`, { ns: 'account' })}
              </SpecialSpan>
            </div>
          ) : (
            <Button
              variant="dark"
              className="mb-3 w-full md:mb-0"
              status={status as any}
              onClick={handleClick}
              data-bottom-element
            >
              {t(`privileges.${dafItem.privilege_action}`, { ns: 'account' })}
            </Button>
          )}
        </div>
      </div>
      {typeof modalDescription === 'string' && (
        <PrivilegesModal
          handleRequestClose={() => setModalDescription(null)}
          dafDescription={dafItem.description}
        />
      )}
    </>
  )

  async function handleClick() {
    if (!dafItem) {
      return addNotification({
        variant: 'error',
        label: t('somethingWentWrong', { ns: 'user-errors' }),
      })
    }

    if (dafItem.privilege_action === 'RESERVATION') {
      setModalDescription(dafItem.description)
    }

    if (dafItem.privilege_action === 'PartnerURL' && elements.partner_url__optional_.value) {
      window.open(elements.partner_url__optional_.value)
    }

    if (dafItem.privilege_action === 'EVENTS' && elements.event_url__optional_.value) {
      window.open(elements.event_url__optional_.value)
    }

    if (dafItem.privilege_action === 'SALESORDER') {
      setStatus('loading')
      addPrivilegeItemToCart(
        dafItem.privilege_nr.toString(),
        getKontentImage(elements.image___placeholder_image.value[0].url, {
          width: 1024,
        }),
        elements.title.value,
      )
    }
  }
}
